import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { ALL_PREFECTURES } from '../constants/prefectures';
import { getCitiesByPrefecture } from '../constants/cities';

const JobSeekerProfileSetup = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    prefecture: user?.prefecture || '',
    city: user?.city || '',
    address: user?.address || '',
    preferred_work_prefecture: '',
    preferred_work_city: '',
    self_introduction: user?.self_introduction || '',
    birth_date: user?.birth_date || '',
    gender: user?.gender || '',
    experience_level: 'beginner' // 初心者, 経験者, エキスパート
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // エラークリア
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '氏名は必須です';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号は必須です';
    }
    if (!formData.prefecture) {
      newErrors.prefecture = '都道府県は必須です';
    }
    if (!formData.city) {
      newErrors.city = '市区町村は必須です';
    }
    if (!formData.address.trim()) {
      newErrors.address = '住所は必須です';
    }
    if (!formData.preferred_work_prefecture) {
      newErrors.preferred_work_prefecture = '希望勤務地の都道府県は必須です';
    }
    if (!formData.preferred_work_city) {
      newErrors.preferred_work_city = '希望勤務地の市区町村は必須です';
    }
    if (!formData.birth_date) {
      newErrors.birth_date = '生年月日は必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('すべての必須項目を入力してください');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.put('/mypage/profile', formData);

      if (response.status !== 200) {
        throw new Error(response.data?.detail || 'プロフィールの更新に失敗しました');
      }

      const updatedUser = response.data;
      updateUser(updatedUser);
      toast.success('プロフィールを保存しました！');
      navigate('/seeker-dashboard');
    } catch (error) {
      console.error('Profile setup error:', error);
      toast.error('プロフィールの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <div className="bg-blue-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <UserIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">求職者プロフィール設定</h1>
          <p className="text-gray-600 mt-2">
            あなたの詳細情報を入力して、プロフィールを完成させましょう
          </p>
        </div>

        {/* プログレスバー */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
                <span className="ml-2 text-sm font-medium text-green-600">アカウント作成</span>
              </div>
              <div className="w-16 h-1 bg-blue-200 rounded-full">
                <div className="w-full h-full bg-blue-500 rounded-full"></div>
              </div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full">
                  <span className="text-white text-sm font-medium">2</span>
                </div>
                <span className="ml-2 text-sm font-medium text-blue-600">プロフィール設定</span>
              </div>
              <div className="w-16 h-1 bg-gray-200 rounded-full"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                  <span className="text-gray-500 text-sm font-medium">3</span>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-500">完了</span>
              </div>
            </div>
          </div>
        </div>

        {/* フォーム */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 基本情報 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                  基本情報
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      氏名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="山田太郎"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="090-1234-5678"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      生年月日 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="birth_date"
                      value={formData.birth_date}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.birth_date ? 'border-red-300' : 'border-gray-300'
                        }`}
                    />
                    {errors.birth_date && (
                      <p className="text-red-500 text-sm mt-1">{errors.birth_date}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      性別
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">選択してください</option>
                      <option value="male">男性</option>
                      <option value="female">女性</option>
                      <option value="other">その他</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 住所情報 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-blue-600" />
                  住所情報
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      都道府県 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="prefecture"
                      value={formData.prefecture}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.prefecture ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {ALL_PREFECTURES.map((prefecture) => (
                        <option key={prefecture.name} value={prefecture.name}>
                          {prefecture.name}
                        </option>
                      ))}
                    </select>
                    {errors.prefecture && (
                      <p className="text-red-500 text-sm mt-1">{errors.prefecture}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      市区町村 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={!formData.prefecture}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.city ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {formData.prefecture &&
                        getCitiesByPrefecture(formData.prefecture).map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                    </select>
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      住所 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.address ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="1-1-1 マンション名 部屋番号"
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 希望勤務地 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-green-600" />
                  希望勤務地
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      都道府県 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="preferred_work_prefecture"
                      value={formData.preferred_work_prefecture}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.preferred_work_prefecture ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {ALL_PREFECTURES.map((prefecture) => (
                        <option key={prefecture.name} value={prefecture.name}>
                          {prefecture.name}
                        </option>
                      ))}
                    </select>
                    {errors.preferred_work_prefecture && (
                      <p className="text-red-500 text-sm mt-1">{errors.preferred_work_prefecture}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      市区町村 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="preferred_work_city"
                      value={formData.preferred_work_city}
                      onChange={handleChange}
                      disabled={!formData.preferred_work_prefecture}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.preferred_work_city ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">選択してください</option>
                      {formData.preferred_work_prefecture &&
                        getCitiesByPrefecture(formData.preferred_work_prefecture).map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                    </select>
                    {errors.preferred_work_city && (
                      <p className="text-red-500 text-sm mt-1">{errors.preferred_work_city}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 経験レベル・自己紹介 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-purple-600" />
                  その他の情報
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      経験レベル
                    </label>
                    <select
                      name="experience_level"
                      value={formData.experience_level}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="beginner">初心者</option>
                      <option value="intermediate">経験者</option>
                      <option value="expert">エキスパート</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      自己紹介文
                    </label>
                    <textarea
                      name="self_introduction"
                      value={formData.self_introduction}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="あなたの経験やアピールポイントを書いてください..."
                    />
                  </div>
                </div>
              </div>

              {/* ボタン */}
              <div className="flex gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/seeker-dashboard')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  後で設定する
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '保存中...' : 'プロフィールを保存する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerProfileSetup;